import { create } from 'zustand'
import { getEnv } from '@/lib/utils'
import { getGatewayApiKey } from '@/lib/gateway-auth'

interface Message {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp: Date
  metadata?: {
    model?: string
    tokens?: number
    tools?: string[]
  }
}

interface GatewayPayload {
  content?: string
  message?: string
  text?: string
  tools?: string[]
  code?: string
  [key: string]: unknown
}

interface GatewayIncoming {
  id?: string
  type?: string
  timestamp?: string
  source?: string
  channel?: string
  payload?: GatewayPayload
  metadata?: Record<string, unknown>
}

interface WebSocketState {
  socket: WebSocket | null
  isConnected: boolean
  messages: Message[]
  isTyping: boolean
  currentSession: string | null
  connect: () => void
  disconnect: () => void
  sendMessage: (content: string, tools?: string[]) => void
  clearMessages: () => void
  addMessage: (message: Message) => void
}

/** Gateway serves native WebSocket at `/ws` (not Socket.IO). */
function getGatewayWsUrl(): string {
  const env = getEnv('VITE_WS_URL')
  if (env) return env
  if (typeof window === 'undefined') return 'ws://localhost:8080/ws'
  const { protocol, hostname, port, host } = window.location
  const wsProto = protocol === 'https:' ? 'wss:' : 'ws:'
  if (port === '3002' || port === '3001' || port === '5173') {
    return `${wsProto}//${hostname}:8080/ws`
  }
  return `${wsProto}//${host}/ws`
}

function extractAssistantText(payload: GatewayPayload | undefined): string | null {
  if (!payload) return null
  if (typeof payload.content === 'string') return payload.content
  if (typeof payload.message === 'string') return payload.message
  if (typeof payload.text === 'string') return payload.text
  return null
}

let typingFallbackTimer: ReturnType<typeof setTimeout> | null = null

function scheduleTypingFallback(
  set: (partial: Partial<WebSocketState> | ((s: WebSocketState) => Partial<WebSocketState>)) => void
) {
  if (typingFallbackTimer) clearTimeout(typingFallbackTimer)
  typingFallbackTimer = setTimeout(() => {
    set({ isTyping: false })
    typingFallbackTimer = null
  }, 15000)
}

function clearTypingFallback() {
  if (typingFallbackTimer) {
    clearTimeout(typingFallbackTimer)
    typingFallbackTimer = null
  }
}

export const useWebSocketStore = create<WebSocketState>((set, get) => ({
  socket: null,
  isConnected: false,
  messages: [],
  isTyping: false,
  currentSession: null,

  connect: () => {
    const existing = get().socket
    if (existing && (existing.readyState === WebSocket.OPEN || existing.readyState === WebSocket.CONNECTING)) {
      return
    }

    const url = getGatewayWsUrl()
    const ws = new WebSocket(url)

    ws.onopen = () => {
      const payload: Record<string, unknown> = {
        clientId: crypto.randomUUID(),
        clientType: 'user',
      }
      const key = getGatewayApiKey()
      if (key) payload.apiKey = key

      try {
        ws.send(
          JSON.stringify({
            id: crypto.randomUUID(),
            type: 'CONNECT',
            timestamp: new Date().toISOString(),
            source: 'web-dashboard',
            channel: 'system',
            priority: 'normal',
            direction: 'incoming',
            payload,
            metadata: {},
          }),
        )
      } catch (e) {
        console.error('Gateway CONNECT send failed:', e)
      }

      set({ isConnected: true, socket: ws })
    }

    ws.onclose = () => {
      clearTypingFallback()
      set({ isConnected: false, socket: null, isTyping: false })
    }

    ws.onerror = () => {
      console.error('WebSocket connection error:', url)
      set({ isConnected: false })
    }

    ws.onmessage = (ev: MessageEvent<string>) => {
      try {
        const data = JSON.parse(ev.data) as GatewayIncoming
        const type = data.type

        if (type === 'ERROR') {
          const msg =
            typeof data.payload?.message === 'string'
              ? data.payload.message
              : 'Gateway error'
          console.error('Gateway ERROR:', msg, data.payload)
          clearTypingFallback()
          set({ isTyping: false })
          return
        }

        if (type === 'CONNECT' || type === 'HEARTBEAT_ACK') {
          return
        }

        if (type === 'RESPONSE' || type === 'MESSAGE' || type === 'BROADCAST' || type === 'SYSTEM') {
          const text = extractAssistantText(data.payload)
          if (!text) return
          if (data.source === 'web-dashboard') return

          clearTypingFallback()
          set((state) => ({
            messages: [
              ...state.messages,
              {
                id: data.id ?? crypto.randomUUID(),
                role: 'assistant',
                content: text,
                timestamp: data.timestamp ? new Date(data.timestamp) : new Date(),
                metadata: {
                  model: typeof data.metadata?.model === 'string' ? data.metadata.model : undefined,
                  tokens:
                    typeof data.metadata?.tokens === 'number' ? data.metadata.tokens : undefined,
                },
              },
            ],
            isTyping: false,
          }))
        }
      } catch (e) {
        console.warn('Non-JSON or invalid gateway message:', ev.data, e)
      }
    }

    set({ socket: ws })
  },

  disconnect: () => {
    clearTypingFallback()
    const { socket } = get()
    if (socket) {
      socket.close()
      set({ socket: null, isConnected: false, isTyping: false })
    }
  },

  sendMessage: (content: string, tools?: string[]) => {
    const { socket } = get()
    if (!socket || socket.readyState !== WebSocket.OPEN) return

    const userMessage: Message = {
      id: crypto.randomUUID(),
      role: 'user',
      content,
      timestamp: new Date(),
    }

    set((state) => ({
      messages: [...state.messages, userMessage],
      isTyping: true,
    }))

    const payload: GatewayPayload = { content }
    if (tools && tools.length > 0) payload.tools = tools

    const outgoing: Record<string, unknown> = {
      id: crypto.randomUUID(),
      type: 'MESSAGE',
      timestamp: new Date().toISOString(),
      source: 'web-dashboard',
      channel: 'local',
      priority: 'normal',
      direction: 'incoming',
      payload,
      metadata: {},
    }

    const sid = get().currentSession
    if (sid) outgoing.sessionId = sid

    socket.send(JSON.stringify(outgoing))
    scheduleTypingFallback(set)
  },

  clearMessages: () => {
    set({ messages: [] })
  },

  addMessage: (message: Message) => {
    set((state) => ({
      messages: [...state.messages, message],
    }))
  },
}))
